import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
    ArrowLeft,
    Clock,
    Eye,
    Bookmark,
    BookmarkCheck,
    Star,
    AlertCircle,
    CheckCircle,
    ThumbsUp,
    ThumbsDown,
    List,
    Play,
    Video,
    BookOpen,
    ExternalLink,
    FileText,
    Edit2
} from 'lucide-react';
import resourceService from '../../services/resource.service';
import ResourceCard from '../../components/resources/ResourceCard';
import YouTubePlayerModal from '../../components/resources/YouTubePlayerModal';

const ResourceDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [resource, setResource] = useState(null);
    const [userProgress, setUserProgress] = useState(null);
    const [relatedResources, setRelatedResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isBookmarked, setIsBookmarked] = useState(false);
    const [rating, setRating] = useState(0);
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [tableOfContents, setTableOfContents] = useState([]);
    const [activeSection, setActiveSection] = useState('');
    const [processedContent, setProcessedContent] = useState('');
    const [showMobileTOC, setShowMobileTOC] = useState(false);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showVideoModal, setShowVideoModal] = useState(false);

    const handlePlayVideo = (video) => {
        setSelectedVideo(video);
        setShowVideoModal(true);
    };

    const handleCloseVideoModal = () => {
        setShowVideoModal(false);
        setSelectedVideo(null);
    };

    useEffect(() => {
        const fetchResource = async () => {
            try {
                setLoading(true);
                const response = await resourceService.getResourceBySlug(slug);
                setResource(response.data.resource);
                setUserProgress(response.data.userProgress);
                setIsBookmarked(response.data.userProgress?.bookmarked || false);
                setRating(response.data.userProgress?.rating || 0);
                setRelatedResources(response.data.resource.relatedResources || []);
            } catch (err) {
                setError(err.message || 'Failed to load resource');
            } finally {
                setLoading(false);
            }
        };

        fetchResource();
    }, [slug]);

    // Parse content and generate table of contents
    useEffect(() => {
        if (resource?.content) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(resource.content, 'text/html');
            const headings = doc.querySelectorAll('h2');

            const tocItems = Array.from(headings).map((heading, index) => ({
                id: `section-${index}`,
                text: heading.textContent.trim()
            }));

            setTableOfContents(tocItems);

            // Process content to add section wrappers and IDs
            let modifiedContent = resource.content;

            // First, add IDs to h2 headings
            let h2Count = 0;
            modifiedContent = modifiedContent.replace(/<h2>/g, () => {
                const id = `section-${h2Count}`;
                h2Count++;
                return `<h2 id="${id}">`;
            });

            // Wrap each section (from h2 to next h2 or end) in a styled container
            const sections = modifiedContent.split(/<h2 id="section-\d+">/);
            const processedSections = sections.map((section, index) => {
                if (index === 0) return section; // Content before first h2

                const sectionId = `section-${index - 1}`;
                const h2Match = modifiedContent.match(new RegExp(`<h2 id="${sectionId}">([^<]+)</h2>`));
                const h2Title = h2Match ? h2Match[1] : '';

                return `
                    <div class="content-section mb-8 p-6 bg-gradient-to-br from-gray-50 to-white rounded-lg border border-gray-200 shadow-sm">
                        <h2 id="${sectionId}" class="section-heading">${h2Title}</h2>
                        ${section}
                    </div>
                `;
            }).join('');

            setProcessedContent(processedSections);
        }
    }, [resource]);

    // Track active section on scroll
    useEffect(() => {
        const handleScroll = () => {
            const sections = tableOfContents.map(item =>
                document.getElementById(item.id)
            ).filter(Boolean);

            const scrollPosition = window.scrollY + 150;

            for (let i = sections.length - 1; i >= 0; i--) {
                if (sections[i] && sections[i].offsetTop <= scrollPosition) {
                    setActiveSection(tableOfContents[i].id);
                    break;
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Set initial active section

        return () => window.removeEventListener('scroll', handleScroll);
    }, [tableOfContents]);

    const handleBookmark = async () => {
        try {
            await resourceService.toggleBookmark(resource._id);
            setIsBookmarked(!isBookmarked);
        } catch (err) {
            console.error('Error bookmarking resource:', err);
        }
    };

    const handleRating = async (newRating) => {
        try {
            setRating(newRating);
            await resourceService.rateResource(resource._id, newRating, feedback);
            setShowFeedback(false);
            setFeedback('');
        } catch (err) {
            console.error('Error rating resource:', err);
        }
    };

    const handleMarkComplete = async () => {
        try {
            await resourceService.updateProgress(resource._id, {
                completed: true,
                progress: 100
            });
            setUserProgress({ ...userProgress, completed: true, progress: 100 });
        } catch (err) {
            console.error('Error marking as complete:', err);
        }
    };

    const handleHelpful = async (isHelpful) => {
        try {
            await resourceService.updateProgress(resource._id, {
                helpful: isHelpful
            });
        } catch (err) {
            console.error('Error submitting feedback:', err);
        }
    };

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const offset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setShowMobileTOC(false);
        }
    };

    const formatCategoryName = (category) => {
        return category
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !resource) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-12">
                <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <AlertCircle className="h-5 w-5 text-red-400" />
                        <p className="ml-3 text-sm text-red-700">{error || 'Resource not found'}</p>
                    </div>
                </div>
                <button
                    onClick={() => navigate(`/${user.role}/resources`)}
                    className="mt-4 text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Resources
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                    >
                        <ArrowLeft className="h-5 w-5" />
                        Back
                    </button>

                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded-full mb-3">
                                {formatCategoryName(resource.category)}
                            </span>
                            <h1 className="text-3xl font-bold text-gray-900 mb-3">
                                {resource.title}
                            </h1>
                            <p className="text-lg text-gray-600 mb-4">
                                {resource.description}
                            </p>

                            {/* Meta Info */}
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                {resource.readTime && (
                                    <div className="flex items-center gap-1">
                                        <Clock className="h-4 w-4" />
                                        {resource.readTime} min read
                                    </div>
                                )}
                                <div className="flex items-center gap-1">
                                    <Eye className="h-4 w-4" />
                                    {resource.views} views
                                </div>
                                {resource.difficulty && (
                                    <span className="px-2 py-0.5 bg-gray-100 rounded">
                                        {resource.difficulty}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-2 ml-4">
                            {/* Edit button for admin/counselor/hod */}
                            {['admin', 'counselor', 'hod'].includes(user?.role) && (
                                <button
                                    onClick={() => navigate(`/${user.role}/resources/${slug}/edit`)}
                                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                                >
                                    <Edit2 className="h-4 w-4" />
                                    Edit
                                </button>
                            )}
                            <button
                                onClick={handleBookmark}
                                className={`p-2 rounded-lg transition-colors ${isBookmarked
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                title={isBookmarked ? 'Bookmarked' : 'Bookmark'}
                            >
                                {isBookmarked ? (
                                    <BookmarkCheck className="h-5 w-5" />
                                ) : (
                                    <Bookmark className="h-5 w-5" />
                                )}
                            </button>

                            {!userProgress?.completed && (
                                <button
                                    onClick={handleMarkComplete}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                                >
                                    <CheckCircle className="h-5 w-5" />
                                    Mark as Complete
                                </button>
                            )}

                            {userProgress?.completed && (
                                <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg flex items-center gap-2">
                                    <CheckCircle className="h-5 w-5" />
                                    Completed
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile TOC Toggle */}
            {tableOfContents.length > 0 && (
                <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
                    <button
                        onClick={() => setShowMobileTOC(!showMobileTOC)}
                        className="flex items-center gap-2 text-gray-700 font-medium"
                    >
                        <List className="h-5 w-5" />
                        Table of Contents
                    </button>

                    {showMobileTOC && (
                        <nav className="mt-4 pb-2">
                            <ul className="space-y-2">
                                {tableOfContents.map((item) => (
                                    <li key={item.id}>
                                        <button
                                            onClick={() => scrollToSection(item.id)}
                                            className={`block w-full text-left py-2 px-3 rounded text-sm transition-colors ${activeSection === item.id
                                                ? 'bg-blue-100 text-blue-700 font-medium'
                                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                }`}
                                        >
                                            {item.text}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    )}
                </div>
            )}

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex gap-8">
                    {/* Table of Contents - Desktop */}
                    {tableOfContents.length > 0 && (
                        <aside className="hidden lg:block w-64 flex-shrink-0">
                            <nav className="sticky top-24">
                                <h3 className="font-semibold text-gray-900 mb-4 text-lg">Contents</h3>
                                <ul className="space-y-1">
                                    {tableOfContents.map((item) => (
                                        <li key={item.id}>
                                            <button
                                                onClick={() => scrollToSection(item.id)}
                                                className={`block w-full text-left py-2 px-3 rounded text-sm transition-colors ${activeSection === item.id
                                                    ? 'bg-blue-100 text-blue-700 font-medium border-l-2 border-blue-600'
                                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {item.text}
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </nav>
                        </aside>
                    )}

                    {/* Content Area */}
                    <div className="flex-1 min-w-0">
                        <div className="mb-8">
                            <style>{`
                                .content-section {
                                    scroll-margin-top: 120px;
                                }
                                .section-heading {
                                    margin-top: 0 !important;
                                    padding-bottom: 0.75rem;
                                    border-bottom: 2px solid #e5e7eb;
                                    margin-bottom: 1.5rem;
                                    color: #1f2937;
                                    font-size: 1.75rem;
                                    font-weight: 700;
                                }
                            `}</style>
                            <div
                                className="prose prose-lg max-w-none
                                    prose-headings:font-bold
                                    prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-3 prose-h3:text-blue-700
                                    prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
                                    prose-ul:my-4 prose-ul:space-y-2 prose-li:my-0 prose-li:text-gray-700
                                    prose-ol:my-4 prose-ol:space-y-2
                                    prose-strong:text-gray-900 prose-strong:font-semibold
                                    prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline"
                                dangerouslySetInnerHTML={{ __html: processedContent }}
                            />
                        </div>

                        {/* YouTube Videos Section */}
                        {resource.youtubeVideos?.length > 0 && (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-6">
                                    <Video className="h-6 w-6 text-red-600" />
                                    <h3 className="text-2xl font-bold text-gray-900">Related Videos</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {resource.youtubeVideos.map((video, index) => (
                                        <div
                                            key={index}
                                            onClick={() => handlePlayVideo(video)}
                                            className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden cursor-pointer group hover:shadow-lg transition-all duration-200"
                                        >
                                            {/* Thumbnail */}
                                            <div className="relative aspect-video bg-gray-100">
                                                <img
                                                    src={video.thumbnailUrl || `https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                                                    alt={video.title}
                                                    className="w-full h-full object-cover"
                                                />
                                                {/* Play Button Overlay */}
                                                <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <div className="bg-red-600 rounded-full p-4">
                                                        <Play className="h-8 w-8 text-white fill-white" />
                                                    </div>
                                                </div>
                                                {/* Duration Badge */}
                                                {video.duration && (
                                                    <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/75 text-white text-xs font-medium rounded">
                                                        {video.duration}
                                                    </span>
                                                )}
                                            </div>
                                            {/* Video Info */}
                                            <div className="p-4">
                                                <h4 className="font-semibold text-gray-900 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                                    {video.title}
                                                </h4>
                                                {video.description && (
                                                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                                        {video.description}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* E-books Section */}
                        {resource.ebooks?.length > 0 && (
                            <div className="mb-8">
                                <div className="flex items-center gap-2 mb-6">
                                    <BookOpen className="h-6 w-6 text-green-600" />
                                    <h3 className="text-2xl font-bold text-gray-900">Free E-books & Resources</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {resource.ebooks.map((ebook, index) => (
                                        <a
                                            key={index}
                                            href={ebook.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-white rounded-lg shadow border border-gray-200 p-5 hover:shadow-lg hover:border-green-300 transition-all duration-200 group"
                                        >
                                            <div className="flex items-start gap-4">
                                                <div className="flex-shrink-0 p-3 bg-green-100 rounded-lg">
                                                    <FileText className="h-8 w-8 text-green-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-start justify-between gap-2">
                                                        <h4 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">
                                                            {ebook.title}
                                                        </h4>
                                                        <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                    </div>
                                                    {ebook.author && (
                                                        <p className="text-sm text-gray-500 mt-1">
                                                            By {ebook.author}
                                                        </p>
                                                    )}
                                                    {ebook.description && (
                                                        <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                                            {ebook.description}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-3 mt-3">
                                                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-700 text-xs font-medium rounded">
                                                            {ebook.format || 'PDF'}
                                                        </span>
                                                        {ebook.pages && (
                                                            <span className="text-xs text-gray-500">
                                                                {ebook.pages} pages
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Feedback Section */}
                        <div className="bg-white rounded-lg shadow border border-gray-200 p-6 mb-8">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Was this resource helpful?</h3>

                            <div className="flex items-center gap-4 mb-4">
                                <button
                                    onClick={() => handleHelpful(true)}
                                    className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 hover:bg-green-100 rounded-lg transition-colors"
                                >
                                    <ThumbsUp className="h-5 w-5" />
                                    Yes, helpful
                                </button>
                                <button
                                    onClick={() => handleHelpful(false)}
                                    className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 hover:bg-red-100 rounded-lg transition-colors"
                                >
                                    <ThumbsDown className="h-5 w-5" />
                                    Not helpful
                                </button>
                            </div>

                            {/* Rating */}
                            <div className="pt-4 border-t border-gray-200">
                                <p className="text-sm font-medium text-gray-700 mb-2">Rate this resource:</p>
                                <div className="flex items-center gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            onClick={() => setRating(star)}
                                            onDoubleClick={() => handleRating(star)}
                                            className="focus:outline-none"
                                        >
                                            <Star
                                                className={`h-6 w-6 ${star <= rating
                                                    ? 'fill-yellow-400 text-yellow-400'
                                                    : 'text-gray-300'
                                                    }`}
                                            />
                                        </button>
                                    ))}
                                    {rating > 0 && (
                                        <button
                                            onClick={() => setShowFeedback(!showFeedback)}
                                            className="ml-4 text-sm text-blue-600 hover:text-blue-800"
                                        >
                                            {showFeedback ? 'Cancel' : 'Add feedback'}
                                        </button>
                                    )}
                                </div>

                                {showFeedback && (
                                    <div className="mt-4">
                                        <textarea
                                            value={feedback}
                                            onChange={(e) => setFeedback(e.target.value)}
                                            placeholder="Share your feedback (optional)"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            rows="3"
                                        />
                                        <button
                                            onClick={() => handleRating(rating)}
                                            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                        >
                                            Submit Rating
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Related Resources */}
                        {relatedResources.length > 0 && (
                            <div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-6">Related Resources</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {relatedResources.map((related) => (
                                        <ResourceCard key={related._id} resource={related} />
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* YouTube Player Modal */}
            <YouTubePlayerModal
                isOpen={showVideoModal}
                onClose={handleCloseVideoModal}
                video={selectedVideo}
            />
        </div>
    );
};

export default ResourceDetailPage;
